import { useCallback } from 'react';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import { getServiceIndexByServiceIds } from '~/components/Home/Tiles/Services';
import { createLocationUrlFromLocation } from '~/core/url/location';
import useUniversalSearchByService from '~/hooks/useUniversalSearchByService';
import history from '~/core/history';
import {
  getGroupId,
  getLatitudeLongitude,
  isSolvPartner,
  isTelemedLocation,
} from '~/core/util/location';
import { apiGetJson } from '~/core/dapi';
import { getClosestPhysicalLocationsInGroupWithServiceIdsUrl } from '~/core/dapi/location';
import { METERS_IN_A_MILE, SEARCH_RADIUS_LOCATIONS_WITHIN_SAME_GROUP } from '../config/index';
import logger from '../core/logger/index';
import { useSelectUserLatLong } from '~/components/AccountGetsMarried/components/GetCare/hooks/useSelectUserPosition';

/**
 * Whether or not we should route to the preferred physical location (instead of the SRP)
 *
 * @param prioritizedLocation {LocationInterface} a location object that is your last booked location
 * @param prioritizedLocationOffersService {boolean} boolean that indicates whether the preferred clinic offers a specific service
 * @returns {boolean} true if yes
 */
export const shouldRouteToPrioritizedLocation = (
  prioritizedLocation: LocationInterface,
  prioritizedLocationOffersService: boolean
) => {
  return prioritizedLocationOffersService && isSolvPartner(prioritizedLocation);
};

/**
 * Hook for determining whether to route to a specific clinic CDP based upon the availability of services at that location;
 * if service(s) aren't available, hook will dispatch to find another CDP within that group that has the necessary service(s),
 * (product decision: even if that other CDP is not a bookable CDP due to is_bookable/has_boost/is_viewable false),
 * or to call another hook that will dispatch a universal search.
 *
 * @param analyticsTracking {() => void} a function that logs event tracking
 * @param prioritizedLocation {LocationInterface} a location object that is your last booked location
 * @param prioritizedLocationOffersService {boolean} boolean that indicates whether the preferred clinic offers a specific service
 * @param serviceIds {string[]} a text array of service id hashes
 * @returns {Function} callback function
 */
export const useHandleRouteCDPorUniversalSearch = (
  analyticsTracking: () => void,
  prioritizedLocation: LocationInterface,
  prioritizedLocationOffersService: boolean,
  serviceIds: string[]
) => {
  const searchByService = useUniversalSearchByService(getServiceIndexByServiceIds(serviceIds));
  const userLatitudeLongitude = useSelectUserLatLong();
  return useCallback(async () => {
    analyticsTracking();

    if (shouldRouteToPrioritizedLocation(prioritizedLocation, prioritizedLocationOffersService)) {
      history.push(createLocationUrlFromLocation(prioritizedLocation));
    } else {
      const { longitude: lng, latitude: lat } =
        (!isTelemedLocation(prioritizedLocation) && getLatitudeLongitude(prioritizedLocation)) ||
        userLatitudeLongitude;

      const groupId = getGroupId(prioritizedLocation);

      if (!groupId || !lat || !lng) {
        searchByService();
        return;
      }

      apiGetJson(
        getClosestPhysicalLocationsInGroupWithServiceIdsUrl(
          groupId,
          lat,
          lng,
          SEARCH_RADIUS_LOCATIONS_WITHIN_SAME_GROUP * METERS_IN_A_MILE,
          serviceIds
        )
      )
        .then((json: any) => {
          const { data } = json;
          if (data.results?.[0]) {
            history.push(createLocationUrlFromLocation(data.results[0]));
          } else {
            searchByService();
          }
        })
        .catch((err: any) => {
          logger.error(
            new Error(
              `Failing to fetch closest physical location in group with service id, with error message: ${err}`
            )
          );
        });
    }
  }, [
    analyticsTracking,
    prioritizedLocation,
    prioritizedLocationOffersService,
    searchByService,
    serviceIds,
    userLatitudeLongitude,
  ]);
};
